import { useState, useCallback } from 'react';
import { useGameStore, useUIStore, useSettingsStore } from '@/stores';
import { SoloTeamChoice } from '@/types/game';
import BaseGameSetup from './BaseGameSetup';
import './SoloGameSetup.css';

/**
 * SoloGameSetup - ソロモードセットアップ画面
 * AIとの対戦設定（チーム選択、難易度選択、プレイヤー名）
 */
const SoloGameSetup = () => {
  const playerName = useSettingsStore((state) => state.player.name);
  const aiDifficulty = useSettingsStore((state) => state.rules.aiDifficulty);
  const initializeGame = useGameStore((state) => state.initializeGame);
  const navigateTo = useUIStore((state) => state.navigateTo);

  const [selectedDifficulty, setSelectedDifficulty] = useState<'easy' | 'medium' | 'hard'>(
    aiDifficulty
  );
  const [selectedTeam, setSelectedTeam] = useState<SoloTeamChoice>('team2');

  /**
   * ゲーム開始ハンドラ
   */
  const handleStartGame = useCallback(() => {
    // チーム選択に応じたプレイヤー名を設定
    let playerNames: string[];
    if (selectedTeam === 'team1') {
      // Team 1（デュオ側）: プレイヤー + AIパートナー vs AI対戦相手
      playerNames = [playerName, 'AI パートナー', 'AI 対戦相手'];
    } else {
      // Team 2（ソロ側）: プレイヤー vs AI 2体
      playerNames = [playerName, 'AI プレイヤー 1', 'AI プレイヤー 2'];
    }

    // ゲームを初期化（ソロモード + チーム選択）
    initializeGame('solo', playerNames, '1', selectedTeam);

    // ゲームボード画面に遷移
    navigateTo('game_board');
  }, [playerName, selectedTeam, initializeGame, navigateTo]);

  return (
    <BaseGameSetup
      mode="solo"
      title="ソロプレイ"
      description="AI と対戦します。チームと難易度を選択してゲームを開始してください。"
    >
      <div className="SoloGameSetup">
        {/* チーム選択 */}
        <div className="SoloGameSetup__section">
          <label className="SoloGameSetup__label">チーム選択</label>
          <div className="SoloGameSetup__team-options">
            <label
              className={`SoloGameSetup__team-option ${
                selectedTeam === 'team1' ? 'SoloGameSetup__team-option--selected' : ''
              }`}
            >
              <input
                type="radio"
                name="team"
                value="team1"
                checked={selectedTeam === 'team1'}
                onChange={() => setSelectedTeam('team1')}
              />
              <div className="SoloGameSetup__team-info">
                <span className="SoloGameSetup__team-name">Team 1（デュオ側）</span>
                <span className="SoloGameSetup__team-desc">
                  AIパートナーと協力して1人のAI敵と戦う
                </span>
                <span className="SoloGameSetup__team-points">持ち点: 60pt</span>
              </div>
            </label>
            <label
              className={`SoloGameSetup__team-option ${
                selectedTeam === 'team2' ? 'SoloGameSetup__team-option--selected' : ''
              }`}
            >
              <input
                type="radio"
                name="team"
                value="team2"
                checked={selectedTeam === 'team2'}
                onChange={() => setSelectedTeam('team2')}
              />
              <div className="SoloGameSetup__team-info">
                <span className="SoloGameSetup__team-name">Team 2（ソロ側）</span>
                <span className="SoloGameSetup__team-desc">
                  1人で2人のAI敵と戦う
                </span>
                <span className="SoloGameSetup__team-points">持ち点: 80pt</span>
              </div>
            </label>
          </div>
        </div>

        {/* AI難易度選択 */}
        <div className="SoloGameSetup__section">
          <label className="SoloGameSetup__label" htmlFor="difficulty">
            AI難易度
          </label>
          <select
            id="difficulty"
            className="SoloGameSetup__select"
            value={selectedDifficulty}
            onChange={(e) =>
              setSelectedDifficulty(e.target.value as 'easy' | 'medium' | 'hard')
            }
          >
            <option value="easy">かんたん - 初心者向け</option>
            <option value="medium">ふつう - バランス型</option>
            <option value="hard">むずかしい - 上級者向け</option>
          </select>
        </div>

        {/* プレイヤー名表示 */}
        <div className="SoloGameSetup__section">
          <label className="SoloGameSetup__label">プレイヤー名</label>
          <div className="SoloGameSetup__player-name">{playerName}</div>
        </div>

        {/* 開始ボタン */}
        <button className="SoloGameSetup__start-button" onClick={handleStartGame}>
          ゲーム開始
        </button>
      </div>
    </BaseGameSetup>
  );
};

export default SoloGameSetup;
